import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import zhTW from 'antd/locale/zh_TW';
import React, { useEffect, useState } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { SWRConfig } from 'swr';

// Import polyfills for Node.js modules

// 页面组件
import AboutPage from './pages/AboutPage';
import ClassroomPage from './pages/ClassroomPage';
import CouponManagement from './pages/CouponManagement';
import DeleteAccountPage from './pages/DeleteAccountPage';
import HomePage from './pages/HomePage';
import LogisticsTracking from './pages/LogisticsTracking';
import OrderManagement from './pages/OrderManagement';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ProductsPage from './pages/ProductsPage';
import ShoppingGuidePage from './pages/ShoppingGuidePage';
import SymptomsPage from './pages/SymptomsPage';
import UsagePage from './pages/UsagePage';

// 组件
import ChatWidget from './components/ChatWidget';
import CouponNotificationModal from './components/CouponNotificationModal';
import Footer from './components/Footer';
import LoginModal from './components/LoginModal';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';

// 上下文提供者
import { CartProvider } from './contexts/CartContext';
import { I18nProvider } from './i18n';

// SWR配置
import { swrConfig } from './hooks/useSWRConfig';

// 样式
import './styles/common.css';

const App: React.FC = () => {
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [pendingCallback, setPendingCallback] = useState<(() => void) | null>(null);
  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [userId, setUserId] = useState<number | undefined>(undefined);

  // 根据语言设置Ant Design的locale
  const getAntdLocale = () => {
    const language = localStorage.getItem('language') || 'zh-TW';
    return language === 'en' ? enUS : zhTW;
  };

  // 监听登录事件，显示未读优惠券
  useEffect(() => {
    const checkUnreadCoupons = () => {
      const userStr = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (userStr && token) {
        try {
          const user = JSON.parse(userStr);
          setUserId(user.id);
          // 延迟500ms显示，确保用户已完全登录
          setTimeout(() => {
            setCouponModalVisible(true);
          }, 500);
        } catch (error) {
          console.error('解析用户信息失败:', error);
        }
      }
    };

    // 监听登录成功事件
    const handleAuthChanged = () => {
      checkUnreadCoupons();
    };

    window.addEventListener('auth-changed', handleAuthChanged);

    // 页面加载时检查一次
    checkUnreadCoupons();

    return () => {
      window.removeEventListener('auth-changed', handleAuthChanged);
    };
  }, []);

  useEffect(() => {
    const handleShowLoginModal = (event: any) => {
      setPendingCallback(() => event.detail.callback);
      setLoginModalVisible(true);
    };

    window.addEventListener('showLoginModal', handleShowLoginModal);

    return () => {
      window.removeEventListener('showLoginModal', handleShowLoginModal);
    };
  }, []);

  const handleLoginSuccess = () => {
    if (pendingCallback) {
      pendingCallback();
      setPendingCallback(null);
    }
  };

  const handleLoginCancel = () => {
    setLoginModalVisible(false);
    setPendingCallback(null);
  };

  return (
    <SWRConfig value={swrConfig}>
      <ConfigProvider locale={getAntdLocale()}>
        <I18nProvider>
          <CartProvider>
            <Router>
              <ScrollToTop />
              <div className="App">
                <Navbar />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/product/:id" element={<ProductDetailPage />} />
                  <Route path="/usage" element={<UsagePage />} />
                  <Route path="/classroom" element={<ClassroomPage />} />
                  <Route path="/orders" element={<OrderManagement />} />
                  <Route path="/logistics" element={<LogisticsTracking />} />
                  <Route path="/symptoms" element={<SymptomsPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/shopping-guide" element={<ShoppingGuidePage />} />
                  <Route path="/coupons" element={<CouponManagement />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/delete-account" element={<DeleteAccountPage />} />
                </Routes>
                <Footer />
              </div>

              <ChatWidget />
            </Router>
          </CartProvider>
        </I18nProvider>
      </ConfigProvider>

      <LoginModal
        visible={loginModalVisible}
        onCancel={handleLoginCancel}
        onSuccess={handleLoginSuccess}
      />

      <CouponNotificationModal
        visible={couponModalVisible}
        onClose={() => setCouponModalVisible(false)}
        userId={userId}
      />
    </SWRConfig>
  );
};

export default App;